import React, {
  ReactElement,
  useEffect,
  useRef,
  useState,
  Fragment,
  useCallback,
} from 'react';
import { debounce } from 'lodash';
import api from '../../services/api';
import Box from '@mui/material/Box';
import {
  Alert,
  Breadcrumbs,
  IconButton,
  Button,
  ListItemAvatar,
  Pagination,
  Stack,
  TextField,
  Typography,
  Dialog, DialogActions,
} from '@mui/material';
import Avatar from '@mui/material/Avatar';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import AttachmentRoundedIcon from '@mui/icons-material/AttachmentRounded';
import Zoom from 'react-medium-image-zoom';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import Tooltip from '@mui/material/Tooltip';
import { comment } from '../../schema/comment';
import { useStateContext } from '../../hooks/useStateContext';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import CloseIcon from '@mui/icons-material/Close';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import CloudDownloadRoundedIcon from '@mui/icons-material/CloudDownloadRounded';
import { toaster } from '../../services/toaster';
import { Client } from '../../services/Client';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import classNames from 'classnames';
import {useSocketContext} from "./contexts/MessageSocketContext";
import {downloadAttachment} from "../../utils/downloadAttachment";

export const CommentWall = ({
  schema,
  values,
  tableType,
  setVisibleColumns,
}: {
  schema: any;
  values: any;
  tableType: string;
  setVisibleColumns: (value: any) => void;
}): ReactElement => {

  const { user } = useStateContext();
  //const [messages, setMessages] = useState<any>([]);
  const [files, setFiles] = useState<any>([]);
  const [filesInComments, setFilesInComments] = useState<any>([]);
  const [message, setMessage] = useState<string>('');
  const ref = useRef<HTMLInputElement>();
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [showConfirm, setShowConfirm] = useState({
    open: false,
    file: undefined,
  });
  const [loadingButton, setLoadingButton] = useState(false);

  const { messages, addMessage } = useSocketContext();

  const handleClickOpen = () => {
    setOpen(true);
  };

  const fetchData = useCallback(() => {
    const searchParams = new URLSearchParams({
      type: tableType,
      reservation_id: values?.id,
    });

    const schema = new Client(`/comment-files`);
    const filterOptions: any = {};
    schema
      .readAll(page, searchParams, '10', filterOptions)
      .then((result: any) => {
        setFilesInComments(result.data);
        setPageCount(result?.last_page);
      });
  }, [page, pageCount]);

  useEffect(() => {
    fetchData();
  }, [page, schema, tableType, open]);

  const handleClose = () => {
    setOpen(false);

    setFilesInComments([]);
    setPage(1);
    setPageCount(0);
  };

  const handleRemoveFile = () => {
    if (showConfirm.file) {
      const removableFile: any = showConfirm.file;
      api
        .delete(`/files/${removableFile?.id}`, {})
        .then((result: any) => {
          const removedArray = filesInComments.filter((item: any) => {
            return item?.id !== removableFile?.id;
          });

          setFilesInComments(removedArray);
          toaster.show({
            intent: 'success',
            message: `Successfully deleted file`,
          });
        })
        .catch((error) => {
          toaster.show({
            icon: 'error',
            message: `Error: ${error}`,
            intent: 'danger',
          });
        });
      setShowConfirm({ file: undefined, open: false });
    }
  };

  const debouncedOnChange = debounce(handleDebouncedOnChange, 300);

  function handleDebouncedOnChange(value: any) {
    setMessage(value);
  }

  const handleChange = (newFiles: any) => {
    const newFilesArray = [...newFiles];
    const oldFiles = [...files];
    newFilesArray.map((file: any) => {
      file.preview = URL.createObjectURL(file);
      oldFiles.push(file);
    });
    setFiles(oldFiles);
  };
  const handleDeleteFile = (id: any) => {
    const newFilesArray = files.filter(
      (item: any, index: number) => index !== id
    );
    setFiles(newFilesArray);
  };

  const sendMessage = () => {
    setLoadingButton(true);

    const sendArray: any = {
      user: user,
      comment: message,
    };

    sendArray[`${tableType}`] = values;
    comment.client.create(sendArray).then((result: any) => {
      if (files.length) {
        //Create files after save the comment data
        let formData = new FormData();
        files.forEach((file: any, i: any) => {
          formData.append(`file-${i}`, file);
        });
        formData.append(`schema`, JSON.stringify(comment));
        formData.append(`column`, 'attachments');
        formData.append(`path`, 'public');
        formData.append(`id`, result?.id);

        api
          .postFile(`/upload-files`, formData)
          .then((res: any) => {
            setFiles([]);
            setMessage('');
            if (ref?.current) {
              ref.current.value = '';
            }
            result['attachments'] = res;
            setLoadingButton(false);
          })
          .catch((error: any) => {});
      } else {
        setFiles([]);
        setMessage('');
        if (ref?.current) {
          ref.current.value = '';
        }

      }
    }).catch(()=>{

    }).finally(()=>{
      setLoadingButton(false);
    });
  };

  const handleDownload = async (attachment: any) => {
    try {
      await downloadAttachment(attachment, api.getFile);
    } catch (error) {
      console.log(error);
      toaster.show({
        icon: 'error',
        message: `Error while download data`,
        intent: 'danger',
      });
    }
    return true;
  };

  const handleChangePage = (event: any, value: any) => {
    setPage(value);
  };

  const bottomRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // csak a chat konténeren belül görgessünk az aljára
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages]);

  return (
    <>
      <Stack
        sx={{
          marginTop: '-5px !important',
          marginBottom: '5px !important',
        }}
        spacing={1}
        direction={'row'}
        alignItems="center"
      >
        <IconButton size={'small'} onClick={handleClickOpen}>
          <InfoOutlinedIcon />
        </IconButton>
        <Breadcrumbs separator="›" aria-label="breadcrumb">
          {schema?.breadcrumb.map((item: any) => {
            return (
              <Typography key="3" color={'#666'} fontSize={12}>
                {item}
              </Typography>
            );
          })}
        </Breadcrumbs>
      </Stack>
      <Box
        ref={containerRef}
        sx={{
          boxShadow: 'inset 0px 0px 4px 0px #00000045',
          minHeight: '100px',
          maxHeight: '450px',
          borderRadius: '10px',
          background: '#fbfbfb',
          padding: '15px',
          overflowY: 'auto',
          border: '1px solid #ededed',
          width: '100%',
        }}
      >
        {messages.map((message: any) => {
          const messageUser = message?.user;
          const date = new Date(message?.created_at);
          date.setMinutes(date.getMinutes() - 1);

          const right = messageUser?.id === user?.id;
          let bgColor = messageUser?.id === user?.id ? 'linear-gradient(332deg, #5798eb, #5dabe1)' : '#ededed';
          let color = messageUser?.id === user?.id ? '#fff' : '#333';

          if (message?.color){
            color = message.color;
          }
          if (message?.background){
            bgColor = message.background;
          }
          return (
            <>
              <Stack
                spacing={1}
                direction="column"
                alignItems={right ? 'flex-end' : 'flex-start'}
                marginTop={0}
                marginBottom={1}
              >
                <div className={'pl-[65px] text-[12px] text-[#666]'}>
                  {messageUser?.name},{' '}
                  <span className={'text-[11px] text-[#ababab]'}>
                    {/*{formatDistanceToNow(date, {
                      addSuffix: true,
                      locale: hu,
                    })}*/}
                    {message?.created_at}
                  </span>
                </div>
                <Stack
                  sx={{
                    marginTop: '0px !important',
                  }}
                  spacing={1}
                  direction={(message?.user?.id === user?.id) ? 'row-reverse' : 'row'}
                  alignItems="flex-start"
                >
                  <Avatar
                    sx={{
                      bgcolor: messageUser?.avatar_color
                        ? messageUser?.avatar_color
                        : '#678fff',
                    }}
                    src={messageUser?.avatar ? messageUser?.avatar : undefined}
                  >
                    {!messageUser?.avatar ? (
                      <>{messageUser?.name ? messageUser.name.charAt(0).toUpperCase() : '-'}</>
                    ) : (
                      <></>
                    )}
                  </Avatar>
                  {message?.comment && (
                    <Box
                      sx={{
                        background: bgColor,
                        color: color,
                      }}
                      className={'rounded-[20px] py-[10px] px-[15px] whitespace-pre-line'}
                      dangerouslySetInnerHTML={{ __html: message?.comment }}
                    >
                    </Box>
                  )}
                </Stack>

                {message?.attachments && message?.attachments.length > 0 && (
                  <Stack
                    sx={{
                      marginTop: '10px !important',
                      width: '100%',
                      flexWrap: 'wrap',
                    }}
                    spacing={1}
                    direction={right ? 'row-reverse' : 'row'}
                    alignItems="flex-start"
                  >
                    {message.attachments.map((attachment: any) => {
                      return (
                        <>
                          <Tooltip title={attachment?.original_name}>
                            <div className={'w-[50px] h-[50px] relative'}>
                              {attachment?.type.includes('image') ? (
                                <Zoom>
                                  <img
                                    className={
                                      'w-[50px] h-[50px] object-cover rounded-[10px]'
                                    }
                                    src={attachment?.link}
                                    alt=""
                                  />
                                </Zoom>
                              ) : (
                                <div
                                  className={
                                    'w-[50px] h-[50px] rounded-[10px] bg-[#e1e1e1]'
                                  }
                                >
                                  <FolderRoundedIcon
                                    sx={{
                                      color: '#b9b9b9',
                                      position: 'absolute',
                                      top: '50%',
                                      left: '50%',
                                      transform: 'translate(-50%,-50%)',
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          </Tooltip>
                        </>
                      );
                    })}
                  </Stack>
                )}
              </Stack>
            </>
          );
        })}
        <div ref={bottomRef} />
      </Box>
      <Stack
        sx={{
          marginTop: '20px !important',
        }}
        spacing={1}
        direction={'row'}
        alignItems="flex-start"
      >
        <TextField
          disabled={loadingButton}
          inputRef={ref}
          id="outlined-multiline-flexible"
          label="Type here..."
          multiline
          size={'small'}
          fullWidth={true}
          maxRows={2}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '30px !important',
              border: '1px solid #ededed',
            },
          }}
          onChange={(event) => debouncedOnChange(event?.target?.value)}
        />

        <input
          disabled={loadingButton}
          accept="*"
          id="icon-button-file"
          type="file"
          style={{ display: 'none' }}
          onChange={(event) => {
            handleChange(event?.target?.files);
          }}
          multiple={true}
        />
        <label htmlFor="icon-button-file">
          <IconButton
            color="primary"
            aria-label="upload picture"
            component="span"
            disabled={loadingButton}
          >
            <AttachmentRoundedIcon />
          </IconButton>
        </label>

        <IconButton
          sx={{
            background: '#3e81fb',
            '&:hover': {
              background: '#2f70e7 !important',
            },
          }}
          onClick={sendMessage}
        >
          <SendRoundedIcon
            sx={{
              color: '#fff',
            }}
          />
        </IconButton>
      </Stack>
      <Stack
        sx={{
          marginTop: '10px !important',
        }}
        spacing={2}
        direction={'row'}
        alignItems="flex-start"
      >
        {files.map((item: any, index: number) => {
          return (
            <>
              <Tooltip title={item?.name}>
                <div className={'relative w-[50px] h-[50px]'}>
                  {item?.type.includes('image') ? (
                    <Zoom>
                      <img
                        src={item?.preview}
                        alt={item?.name}
                        className={'w-[50px] h-[50px] rounded-[10px]'}
                      />
                    </Zoom>
                  ) : (
                    <div
                      className={
                        'w-[50px] h-[50px] rounded-[10px] bg-[#e1e1e1]'
                      }
                    >
                      <FolderRoundedIcon
                        sx={{
                          color: '#b9b9b9',
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%,-50%)',
                        }}
                      />
                    </div>
                  )}
                  <div
                    className={
                      'w-[24px] h-[24px] bg-[#db0000] rounded-full absolute -top-[6px] -right-[9px] hover:bg-[#c30303] cursor-pointer'
                    }
                    onClick={() => {
                      handleDeleteFile(index);
                    }}
                  >
                    <CloseRoundedIcon
                      sx={{
                        color: '#fff',
                      }}
                    />
                  </div>
                </div>
              </Tooltip>
            </>
          );
        })}
      </Stack>

      <Dialog
        onClose={handleClose}
        open={open}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          Files
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent
          dividers
          sx={{
            background: '#f8f8f8',
          }}
        >
          <List
            sx={{
              background: '#fff',
              border: '1px solid #dfdfdf',
              borderRadius: '10px',
              padding: '0px',
              maxHeight: '600px',
              overflowY: 'auto',
            }}
          >
            {!filesInComments.length && (
              <>
                <Alert severity="info">No files have been uploaded yet</Alert>
              </>
            )}
            {filesInComments.map((file: any) => {
              return (
                <>
                  <ListItem
                    sx={{
                      paddingX: '20px',
                      paddingY: '10px',
                      alignItems: 'center',
                      '&:hover': {
                        background: 'rgba(0,0,0,0.02)',
                      },
                    }}
                  >
                    <ListItemAvatar
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Zoom>
                        <Avatar src={file?.link} alt="" />
                      </Zoom>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <React.Fragment>
                          <b className={'text-[12px]'}>{file?.original_name}</b>
                        </React.Fragment>
                      }
                      secondary={
                        <React.Fragment>
                          <span className={'text-[12px]'}>
                            Uploaded by: <b>{file?.user?.name}</b>{' '}
                            <small>{file?.created_at}</small>
                          </span>
                        </React.Fragment>
                      }
                    />
                    {file.delete && (
                      <IconButton
                        size={'small'}
                        onClick={() => {
                          setShowConfirm({ file: file, open: true });
                        }}
                      >
                        <DeleteIcon color={'error'} />
                      </IconButton>
                    )}
                    <IconButton
                      size={'small'}
                      onClick={() => {
                        handleDownload(file);
                      }}
                    >
                      <CloudDownloadRoundedIcon color={'primary'} />
                    </IconButton>
                  </ListItem>
                </>
              );
            })}
          </List>

          <Stack
            alignItems={'center'}
            spacing={2}
            sx={{
              marginTop: '10px',
            }}
          >
            <Pagination
              count={pageCount}
              page={page}
              onChange={handleChangePage}
              variant="outlined"
              color="primary"
            />
          </Stack>
        </DialogContent>
      </Dialog>

      {showConfirm.open && (
        <Dialog
          open={showConfirm?.open}
        >
          <DialogTitle>
            Confirm
          </DialogTitle>
         <DialogActions>
           <Button
             variant='outlined'
             onClick={() => {
               setShowConfirm({ file: undefined, open: false });
             }}
           >
             Cancel
           </Button>
           <Button
             variant='contained'
             color='warning'
             onClick={handleRemoveFile}
           >
             Delete
           </Button>
         </DialogActions>
        </Dialog>
      )}
    </>
  );
};
